import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { 
  CheckCircle, 
  Loader2, 
  Upload, 
  X, 
  FileText,
  Plus,
  Trash2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApplicationPage() {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // Form state
  const [formData, setFormData] = useState({
    // Personal Info
    name: '',
    email: '',
    phone: '',
    entity_name: '',
    entity_type: '',
    
    // Property Address
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_type: '',
    
    // Loan Details
    purchase_price: '',
    loan_amount: '',
    loan_type: '',
    
    // Need of Funds
    need_of_funds: '',
    
    // Exit Plan
    exit_plan: '',
    exit_timeline: '',
    
    // Rent Roll / Contract
    has_rent_roll: '',
    rent_roll_details: '',
    has_purchase_contract: '',
    
    // Schedule of Real Estate
    real_estate_owned: [
      { address: '', value: '', mortgage_balance: '', monthly_rent: '', property_type: '' }
    ],
    
    // Personal Financial Statement
    total_assets: '',
    total_liabilities: '',
    annual_income: '',
    liquid_assets: '',
    credit_score_range: '',
    
    // Additional
    additional_notes: '',
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [rentRollFiles, setRentRollFiles] = useState([]);
  const [contractFiles, setContractFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const handleRentRollUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.size <= 100 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      toast.error('Some files exceed 100MB limit');
    }
    setRentRollFiles(prev => [...prev, ...validFiles]);
  };

  const removeRentRollFile = (index) => {
    setRentRollFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleContractUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.size <= 100 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      toast.error('Some files exceed 100MB limit');
    }
    setContractFiles(prev => [...prev, ...validFiles]);
  };

  const removeContractFile = (index) => {
    setContractFiles(prev => prev.filter((_, i) => i !== index));
  };

  const labels = {
    en: {
      pageTitle: 'Loan Application',
      pageSubtitle: 'Complete Application',
      pageDesc: 'Complete the form below to submit your full loan application. All fields marked with * are required.',
      step1: 'Borrower Info',
      step2: 'Property & Loan',
      step3: 'Financial Statement',
      step4: 'Documents & Submit',
      // Personal Info
      personalInfo: 'Borrower Information',
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      entityName: 'Entity Name (if applicable)',
      entityType: 'Entity Type',
      selectEntityType: 'Select entity type',
      individual: 'Individual',
      llc: 'LLC',
      corporation: 'Corporation',
      partnership: 'Partnership',
      trust: 'Trust',
      // Property
      propertyInfo: 'Subject Property',
      propertyAddress: 'Property Address',
      city: 'City',
      state: 'State',
      zip: 'ZIP Code',
      propertyType: 'Property Type',
      selectPropertyType: 'Select property type',
      singleFamily: 'Single Family',
      multiFamily: 'Multi-Family (2-4 units)',
      multiFamily5: 'Multi-Family (5+ units)',
      commercial: 'Commercial',
      mixedUse: 'Mixed Use',
      land: 'Land',
      // Loan Details
      loanDetails: 'Loan Details',
      purchasePrice: 'Purchase Price / Property Value',
      loanAmount: 'Requested Loan Amount',
      loanType: 'Loan Type',
      selectLoanType: 'Select loan type',
      // Need of Funds
      needOfFunds: 'Purpose / Need of Funds',
      needOfFundsPlaceholder: 'Describe how you plan to use the loan funds (purchase, refinance, rehab costs, etc.)',
      // Exit Plan
      exitPlan: 'Exit Strategy',
      exitPlanLabel: 'Exit Plan',
      exitPlanPlaceholder: 'Describe your exit strategy (sale, refinance to conventional, payoff from other source, etc.)',
      exitTimeline: 'Expected Exit Timeline',
      selectTimeline: 'Select timeline',
      months6: '6 months or less',
      months12: '6-12 months',
      months24: '12-24 months',
      months36: '24-36 months',
      // Rent Roll
      rentRoll: 'Rent Roll / Purchase Contract',
      hasRentRoll: 'Do you have a rent roll?',
      rentRollDetails: 'Rent Roll Details',
      rentRollPlaceholder: 'List current rents: Unit 1 - $X/mo, Unit 2 - $X/mo, etc.',
      hasPurchaseContract: 'Do you have a purchase contract?',
      uploadRentRoll: 'Upload Rent Roll',
      clickToUploadRentRoll: 'Click to upload rent roll document',
      uploadContract: 'Upload Purchase Contract',
      clickToUploadContract: 'Click to upload purchase contract',
      selectOption: 'Select option',
      yes: 'Yes',
      no: 'No',
      na: 'N/A',
      // Schedule of Real Estate
      realEstateSchedule: 'Schedule of Real Estate Owned',
      realEstateDesc: 'List all real estate you currently own',
      addProperty: 'Add Property',
      address: 'Address',
      estimatedValue: 'Est. Value',
      mortgageBalance: 'Mortgage Balance',
      monthlyRent: 'Monthly Rent',
      // Financial Statement
      financialStatement: 'Personal Financial Statement',
      totalAssets: 'Total Assets',
      totalLiabilities: 'Total Liabilities',
      annualIncome: 'Annual Income',
      liquidAssets: 'Liquid Assets (Cash, Stocks, etc.)',
      creditScore: 'Credit Score Range',
      selectCreditScore: 'Select range',
      // Documents
      documents: 'Supporting Documents',
      documentsDesc: 'Mortgage Statements, Property Photos, etc.',
      uploadFiles: 'Upload Files',
      dragDrop: 'Drag & drop files here or click to browse',
      maxSize: 'Max file size: 10MB per file',
      // Additional
      additionalNotes: 'Additional Notes',
      additionalPlaceholder: 'Any additional information you would like to provide...',
      // Navigation
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit Application',
      submitting: 'Submitting...',
      // Success
      thankYou: 'Application Submitted',
      received: 'Thank you for your application. Our team will review your submission and contact you within 24-48 hours.',
      callNow: 'Questions? Call us:',
    },
    zh_CN: {
      pageTitle: '贷款申请',
      pageSubtitle: '完整申请表',
      pageDesc: '请填写以下表格提交完整的贷款申请。标 * 为必填项。',
      step1: '借款人信息',
      step2: '房产与贷款',
      step3: '财务报表',
      step4: '文件与提交',
      personalInfo: '借款人信息',
      name: '姓名',
      email: '电子邮箱',
      phone: '联系电话',
      entityName: '公司名称（如适用）',
      entityType: '实体类型',
      selectEntityType: '请选择实体类型',
      individual: '个人',
      llc: '有限责任公司 (LLC)',
      corporation: '股份公司',
      partnership: '合伙企业',
      trust: '信托',
      propertyInfo: '抵押房产信息',
      propertyAddress: '房产地址',
      city: '城市',
      state: '州',
      zip: '邮编',
      propertyType: '房产类型',
      selectPropertyType: '请选择房产类型',
      singleFamily: '独栋住宅',
      multiFamily: '多户住宅 (2-4单元)',
      multiFamily5: '多户住宅 (5+单元)',
      commercial: '商业地产',
      mixedUse: '混合用途',
      land: '土地',
      loanDetails: '贷款详情',
      purchasePrice: '购买价格 / 房产估值',
      loanAmount: '申请贷款金额',
      loanType: '贷款类型',
      selectLoanType: '请选择贷款类型',
      needOfFunds: '资金用途说明',
      needOfFundsPlaceholder: '请说明贷款资金的用途（购买、再融资、翻新费用等）',
      exitPlan: '退出策略',
      exitPlanLabel: '退出计划',
      exitPlanPlaceholder: '请说明您的退出策略（出售、转为传统贷款、其他来源还款等）',
      exitTimeline: '预计退出时间',
      selectTimeline: '请选择时间',
      months6: '6个月或以内',
      months12: '6-12个月',
      months24: '12-24个月',
      months36: '24-36个月',
      rentRoll: '租金明细 / 购买合同',
      hasRentRoll: '是否有租金明细？',
      rentRollDetails: '租金明细',
      rentRollPlaceholder: '列出当前租金：单元1 - $X/月，单元2 - $X/月，等',
      hasPurchaseContract: '是否有购买合同？',
      uploadRentRoll: '上传租金明细',
      clickToUploadRentRoll: '点击上传租金明细文件',
      uploadContract: '上传购买合同',
      clickToUploadContract: '点击上传购买合同',
      selectOption: '请选择',
      yes: '是',
      no: '否',
      na: '不适用',
      realEstateSchedule: '名下房产清单',
      realEstateDesc: '列出您目前拥有的所有房产',
      addProperty: '添加房产',
      address: '地址',
      estimatedValue: '估值',
      mortgageBalance: '贷款余额',
      monthlyRent: '月租金',
      financialStatement: '个人财务报表',
      totalAssets: '总资产',
      totalLiabilities: '总负债',
      annualIncome: '年收入',
      liquidAssets: '流动资产（现金、股票等）',
      creditScore: '信用分数范围',
      selectCreditScore: '请选择范围',
      documents: '支持文件',
      documentsDesc: '房贷账单、房产照片等',
      uploadFiles: '上传文件',
      dragDrop: '拖放文件到此处或点击浏览',
      maxSize: '单个文件最大：10MB',
      additionalNotes: '补充说明',
      additionalPlaceholder: '您想提供的任何其他信息...',
      next: '下一步',
      previous: '上一步',
      submit: '提交申请',
      submitting: '正在提交...',
      thankYou: '申请已提交',
      received: '感谢您的申请。我们的团队将审核您的申请，并在24-48小时内与您联系。',
      callNow: '有问题？请致电：',
    },
    zh_TW: {
      pageTitle: '貸款申請',
      pageSubtitle: '完整申請表',
      pageDesc: '請填寫以下表格提交完整的貸款申請。標 * 為必填項。',
      step1: '借款人資訊',
      step2: '房產與貸款',
      step3: '財務報表',
      step4: '文件與提交',
      personalInfo: '借款人資訊',
      name: '姓名',
      email: '電子郵箱',
      phone: '聯繫電話',
      entityName: '公司名稱（如適用）',
      entityType: '實體類型',
      selectEntityType: '請選擇實體類型',
      individual: '個人',
      llc: '有限責任公司 (LLC)',
      corporation: '股份公司',
      partnership: '合夥企業',
      trust: '信託',
      propertyInfo: '抵押房產資訊',
      propertyAddress: '房產地址',
      city: '城市',
      state: '州',
      zip: '郵編',
      propertyType: '房產類型',
      selectPropertyType: '請選擇房產類型',
      singleFamily: '獨棟住宅',
      multiFamily: '多戶住宅 (2-4單元)',
      multiFamily5: '多戶住宅 (5+單元)',
      commercial: '商業地產',
      mixedUse: '混合用途',
      land: '土地',
      loanDetails: '貸款詳情',
      purchasePrice: '購買價格 / 房產估值',
      loanAmount: '申請貸款金額',
      loanType: '貸款類型',
      selectLoanType: '請選擇貸款類型',
      needOfFunds: '資金用途說明',
      needOfFundsPlaceholder: '請說明貸款資金的用途（購買、再融資、翻新費用等）',
      exitPlan: '退出策略',
      exitPlanLabel: '退出計劃',
      exitPlanPlaceholder: '請說明您的退出策略（出售、轉為傳統貸款、其他來源還款等）',
      exitTimeline: '預計退出時間',
      selectTimeline: '請選擇時間',
      months6: '6個月或以內',
      months12: '6-12個月',
      months24: '12-24個月',
      months36: '24-36個月',
      rentRoll: '租金明細 / 購買合同',
      hasRentRoll: '是否有租金明細？',
      rentRollDetails: '租金明細',
      rentRollPlaceholder: '列出當前租金：單元1 - $X/月，單元2 - $X/月，等',
      hasPurchaseContract: '是否有購買合同？',
      uploadRentRoll: '上傳租金明細',
      clickToUploadRentRoll: '點擊上傳租金明細文件',
      uploadContract: '上傳購買合同',
      clickToUploadContract: '點擊上傳購買合同',
      selectOption: '請選擇',
      yes: '是',
      no: '否',
      na: '不適用',
      realEstateSchedule: '名下房產清單',
      realEstateDesc: '列出您目前擁有的所有房產',
      addProperty: '添加房產',
      address: '地址',
      estimatedValue: '估值',
      mortgageBalance: '貸款餘額',
      monthlyRent: '月租金',
      financialStatement: '個人財務報表',
      totalAssets: '總資產',
      totalLiabilities: '總負債',
      annualIncome: '年收入',
      liquidAssets: '流動資產（現金、股票等）',
      creditScore: '信用分數範圍',
      selectCreditScore: '請選擇範圍',
      documents: '支持文件',
      documentsDesc: '房貸帳單、房產照片等',
      uploadFiles: '上傳文件',
      dragDrop: '拖放文件到此處或點擊瀏覽',
      maxSize: '單個文件最大：10MB',
      additionalNotes: '補充說明',
      additionalPlaceholder: '您想提供的任何其他資訊...',
      next: '下一步',
      previous: '上一步',
      submit: '提交申請',
      submitting: '正在提交...',
      thankYou: '申請已提交',
      received: '感謝您的申請。我們的團隊將審核您的申請，並在24-48小時內與您聯繫。',
      callNow: '有問題？請致電：',
    },
  };

  const l = labels[language] || labels.en;

  const loanTypes = [
    { value: 'fix_flip', label: t('loanTypes.fixFlip') },
    { value: 'bridge', label: t('loanTypes.bridge') },
    { value: 'rental', label: t('loanTypes.rental') },
    { value: 'commercial', label: t('loanTypes.commercial') },
    { value: 'refinance', label: t('loanTypes.refinance') },
    { value: 'foreclosure', label: t('loanTypes.foreclosure') },
    { value: 'eviction', label: t('loanTypes.eviction') },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRealEstateChange = (index, field, value) => {
    const updated = [...formData.real_estate_owned];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, real_estate_owned: updated }));
  };

  const addRealEstateRow = () => {
    setFormData(prev => ({
      ...prev,
      real_estate_owned: [
        ...prev.real_estate_owned,
        { address: '', value: '', mortgage_balance: '', monthly_rent: '', property_type: '' }
      ]
    }));
  };

  const removeRealEstateRow = (index) => {
    if (formData.real_estate_owned.length > 1) {
      const updated = formData.real_estate_owned.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, real_estate_owned: updated }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.size <= 100 * 1024 * 1024); // 100MB limit
    
    if (validFiles.length !== files.length) {
      toast.error('Some files exceed 100MB limit');
    }
    
    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    // Only validate borrower information (Step 1)
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Required';
      if (!formData.email.trim()) newErrors.email = 'Required';
      if (!formData.phone.trim()) newErrors.phone = 'Required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      // Use FormData for file uploads
      const formDataToSend = new FormData();
      
      // Add all form fields as JSON
      const applicationData = {
        ...formData,
        files_uploaded: [
          ...uploadedFiles.map(f => f.name),
          ...rentRollFiles.map(f => f.name),
          ...contractFiles.map(f => f.name),
        ],
      };
      formDataToSend.append('application_data', JSON.stringify(applicationData));
      
      // Add all uploaded files
      uploadedFiles.forEach((file) => {
        formDataToSend.append('files', file);
      });
      rentRollFiles.forEach((file) => {
        formDataToSend.append('files', file);
      });
      contractFiles.forEach((file) => {
        formDataToSend.append('files', file);
      });

      const response = await axios.post(`${API}/applications`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setSubmitted(true);
        // Google Ads conversion tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17679761657',
            'event_category': 'form',
            'event_label': 'application_form_submission'
          });
        }
        toast.success(l.thankYou, {
          description: l.received,
        });
      }
    } catch (error) {
      console.error('Application submission error:', error);
      toast.error('Submission Failed', {
        description: 'Please try again or call us directly at 424-303-2130',
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50">
        <div className="container-main py-20">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">{l.thankYou}</h1>
            <p className="font-body text-slate-600 text-lg mb-8">{l.received}</p>
            <p className="font-body text-slate-500 mb-2">{l.callNow}</p>
            <a href="tel:424-303-2130" className="phone-link text-2xl font-bold">
              424-303-2130
            </a>
          </div>
        </div>
      </div>
    );
  }

  const stepLabels = [l.step1, l.step2, l.step3, l.step4];

  return (
    <div className="pt-20" data-testid="application-page">
      {/* Hero Section */}
      <section className="bg-slate-900 py-16">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
              {l.pageSubtitle}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              {l.pageTitle}
            </h1>
            <p className="font-body text-slate-300 text-lg">
              {l.pageDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="bg-white border-b border-slate-200 py-6">
        <div className="container-main">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {stepLabels.map((label, index) => (
              <div key={index} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm
                  ${currentStep > index + 1 ? 'bg-green-500 text-white' : 
                    currentStep === index + 1 ? 'bg-slate-900 text-white' : 
                    'bg-slate-200 text-slate-500'}`}
                >
                  {currentStep > index + 1 ? <CheckCircle className="w-5 h-5" /> : index + 1}
                </div>
                <span className={`ml-2 font-body text-sm hidden sm:block
                  ${currentStep === index + 1 ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
                  {label}
                </span>
                {index < stepLabels.length - 1 && (
                  <div className={`w-12 sm:w-20 h-1 mx-2 sm:mx-4 
                    ${currentStep > index + 1 ? 'bg-green-500' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
            
            {/* Step 1: Borrower Info */}
            {currentStep === 1 && (
              <div className="bg-white border border-slate-200 p-8 md:p-10" data-testid="step-1">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.personalInfo}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-body font-medium text-slate-700">{l.name} *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`input-institutional mt-2 rounded-none ${errors.name ? 'border-red-500' : ''}`}
                      data-testid="input-app-name"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>
                  
                  <div>
                    <Label className="font-body font-medium text-slate-700">{l.email} *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`input-institutional mt-2 rounded-none ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="input-app-email"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  
                  <div>
                    <Label className="font-body font-medium text-slate-700">{l.phone} *</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`input-institutional mt-2 rounded-none ${errors.phone ? 'border-red-500' : ''}`}
                      data-testid="input-app-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                  
                  <div>
                    <Label className="font-body font-medium text-slate-700">{l.entityType}</Label>
                    <Select onValueChange={(v) => handleSelectChange('entity_type', v)} value={formData.entity_type}>
                      <SelectTrigger className="input-institutional mt-2 rounded-none">
                        <SelectValue placeholder={l.selectEntityType} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">{l.individual}</SelectItem>
                        <SelectItem value="llc">{l.llc}</SelectItem>
                        <SelectItem value="corporation">{l.corporation}</SelectItem>
                        <SelectItem value="partnership">{l.partnership}</SelectItem>
                        <SelectItem value="trust">{l.trust}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label className="font-body font-medium text-slate-700">{l.entityName}</Label>
                    <Input
                      name="entity_name"
                      value={formData.entity_name}
                      onChange={handleChange}
                      className="input-institutional mt-2 rounded-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Property & Loan */}
            {currentStep === 2 && (
              <div className="space-y-8" data-testid="step-2">
                {/* Property Info */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.propertyInfo}</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Label className="font-body font-medium text-slate-700">{l.propertyAddress}</Label>
                      <Input
                        name="property_address"
                        value={formData.property_address}
                        onChange={handleChange}
                        className="input-institutional mt-2 rounded-none"
                        data-testid="input-property-address"
                      />
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.city}</Label>
                      <Input
                        name="property_city"
                        value={formData.property_city}
                        onChange={handleChange}
                        className="input-institutional mt-2 rounded-none"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-body font-medium text-slate-700">{l.state}</Label>
                        <Input
                          name="property_state"
                          value={formData.property_state}
                          onChange={handleChange}
                          className="input-institutional mt-2 rounded-none"
                        />
                      </div>
                      <div>
                        <Label className="font-body font-medium text-slate-700">{l.zip}</Label>
                        <Input
                          name="property_zip"
                          value={formData.property_zip}
                          onChange={handleChange}
                          className="input-institutional mt-2 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.propertyType}</Label>
                      <Select onValueChange={(v) => handleSelectChange('property_type', v)} value={formData.property_type}>
                        <SelectTrigger className="input-institutional mt-2 rounded-none">
                          <SelectValue placeholder={l.selectPropertyType} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single_family">{l.singleFamily}</SelectItem>
                          <SelectItem value="multi_family_2_4">{l.multiFamily}</SelectItem>
                          <SelectItem value="multi_family_5">{l.multiFamily5}</SelectItem>
                          <SelectItem value="commercial">{l.commercial}</SelectItem>
                          <SelectItem value="mixed_use">{l.mixedUse}</SelectItem>
                          <SelectItem value="land">{l.land}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.loanDetails}</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.purchasePrice}</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          name="purchase_price"
                          value={formData.purchase_price}
                          onChange={handleChange}
                          className="input-institutional pl-8 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.loanAmount}</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          name="loan_amount"
                          value={formData.loan_amount}
                          onChange={handleChange}
                          className="input-institutional pl-8 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.loanType}</Label>
                      <Select onValueChange={(v) => handleSelectChange('loan_type', v)} value={formData.loan_type}>
                        <SelectTrigger className="input-institutional mt-2 rounded-none">
                          <SelectValue placeholder={l.selectLoanType} />
                        </SelectTrigger>
                        <SelectContent>
                          {loanTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Need of Funds & Exit Plan */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.needOfFunds}</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.needOfFunds}</Label>
                      <Textarea
                        name="need_of_funds"
                        value={formData.need_of_funds}
                        onChange={handleChange}
                        className="input-institutional mt-2 rounded-none min-h-[100px]"
                        placeholder={l.needOfFundsPlaceholder}
                        data-testid="input-need-of-funds"
                      />
                    </div>
                  </div>
                </div>

                {/* Exit Plan */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.exitPlan}</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Label className="font-body font-medium text-slate-700">{l.exitPlanLabel}</Label>
                      <Textarea
                        name="exit_plan"
                        value={formData.exit_plan}
                        onChange={handleChange}
                        className="input-institutional mt-2 rounded-none min-h-[100px]"
                        placeholder={l.exitPlanPlaceholder}
                        data-testid="input-exit-plan"
                      />
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.exitTimeline}</Label>
                      <Select onValueChange={(v) => handleSelectChange('exit_timeline', v)} value={formData.exit_timeline}>
                        <SelectTrigger className="input-institutional mt-2 rounded-none">
                          <SelectValue placeholder={l.selectTimeline} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="6_months">{l.months6}</SelectItem>
                          <SelectItem value="12_months">{l.months12}</SelectItem>
                          <SelectItem value="24_months">{l.months24}</SelectItem>
                          <SelectItem value="36_months">{l.months36}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Rent Roll / Contract */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.rentRoll}</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.hasRentRoll}</Label>
                      <Select onValueChange={(v) => handleSelectChange('has_rent_roll', v)} value={formData.has_rent_roll}>
                        <SelectTrigger className="input-institutional mt-2 rounded-none" data-testid="select-rent-roll">
                          <SelectValue placeholder={l.selectOption} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">{l.yes}</SelectItem>
                          <SelectItem value="no">{l.no}</SelectItem>
                          <SelectItem value="na">{l.na}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.hasPurchaseContract}</Label>
                      <Select onValueChange={(v) => handleSelectChange('has_purchase_contract', v)} value={formData.has_purchase_contract}>
                        <SelectTrigger className="input-institutional mt-2 rounded-none" data-testid="select-contract">
                          <SelectValue placeholder={l.selectOption} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">{l.yes}</SelectItem>
                          <SelectItem value="no">{l.no}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.has_rent_roll === 'yes' && (
                      <>
                        <div className="md:col-span-2">
                          <Label className="font-body font-medium text-slate-700">{l.rentRollDetails}</Label>
                          <Textarea
                            name="rent_roll_details"
                            value={formData.rent_roll_details}
                            onChange={handleChange}
                            className="input-institutional mt-2 rounded-none min-h-[100px]"
                            placeholder={l.rentRollPlaceholder}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="font-body font-medium text-slate-700">{l.uploadRentRoll}</Label>
                          <div className="mt-2 border-2 border-dashed border-slate-300 rounded-none p-4 hover:border-slate-400 transition-colors duration-200">
                            <input
                              type="file"
                              multiple
                              onChange={handleRentRollUpload}
                              className="hidden"
                              id="rent-roll-upload"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                            />
                            <label htmlFor="rent-roll-upload" className="cursor-pointer flex items-center justify-center gap-3">
                              <Upload className="w-5 h-5 text-slate-400" />
                              <span className="font-body text-slate-600 text-sm">{l.clickToUploadRentRoll}</span>
                            </label>
                          </div>
                          {rentRollFiles.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {rentRollFiles.map((file, index) => (
                                <div key={index} className="flex items-center justify-between bg-slate-50 p-2 border border-slate-200">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-slate-500" />
                                    <span className="font-body text-sm text-slate-700">{file.name}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeRentRollFile(index)}
                                    className="text-slate-400 hover:text-red-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    
                    {formData.has_purchase_contract === 'yes' && (
                      <div className="md:col-span-2">
                        <Label className="font-body font-medium text-slate-700">{l.uploadContract}</Label>
                        <div className="mt-2 border-2 border-dashed border-slate-300 rounded-none p-4 hover:border-slate-400 transition-colors duration-200">
                          <input
                            type="file"
                            multiple
                            onChange={handleContractUpload}
                            className="hidden"
                            id="contract-upload"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                          <label htmlFor="contract-upload" className="cursor-pointer flex items-center justify-center gap-3">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="font-body text-slate-600 text-sm">{l.clickToUploadContract}</span>
                          </label>
                        </div>
                        {contractFiles.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {contractFiles.map((file, index) => (
                              <div key={index} className="flex items-center justify-between bg-slate-50 p-2 border border-slate-200">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-slate-500" />
                                  <span className="font-body text-sm text-slate-700">{file.name}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeContractFile(index)}
                                  className="text-slate-400 hover:text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Financial Statement */}
            {currentStep === 3 && (
              <div className="space-y-8" data-testid="step-3">
                {/* Schedule of Real Estate */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-2xl font-bold text-slate-900">{l.realEstateSchedule}</h2>
                      <p className="font-body text-slate-600 text-sm mt-1">{l.realEstateDesc}</p>
                    </div>
                    <Button
                      type="button"
                      onClick={addRealEstateRow}
                      className="btn-secondary rounded-none"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {l.addProperty}
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    {formData.real_estate_owned.map((property, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-slate-50 border border-slate-200">
                        <div className="md:col-span-4">
                          <Label className="font-body text-xs text-slate-500">{l.address}</Label>
                          <Input
                            value={property.address}
                            onChange={(e) => handleRealEstateChange(index, 'address', e.target.value)}
                            className="input-institutional mt-1 rounded-none text-sm"
                            placeholder="123 Main St, City, ST"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="font-body text-xs text-slate-500">{l.estimatedValue}</Label>
                          <Input
                            value={property.value}
                            onChange={(e) => handleRealEstateChange(index, 'value', e.target.value)}
                            className="input-institutional mt-1 rounded-none text-sm"
                            placeholder="$500,000"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="font-body text-xs text-slate-500">{l.mortgageBalance}</Label>
                          <Input
                            value={property.mortgage_balance}
                            onChange={(e) => handleRealEstateChange(index, 'mortgage_balance', e.target.value)}
                            className="input-institutional mt-1 rounded-none text-sm"
                            placeholder="$300,000"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="font-body text-xs text-slate-500">{l.monthlyRent}</Label>
                          <Input
                            value={property.monthly_rent}
                            onChange={(e) => handleRealEstateChange(index, 'monthly_rent', e.target.value)}
                            className="input-institutional mt-1 rounded-none text-sm"
                            placeholder="$2,500"
                          />
                        </div>
                        <div className="md:col-span-2 flex items-end">
                          {formData.real_estate_owned.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => removeRealEstateRow(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Personal Financial Statement */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.financialStatement}</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.totalAssets}</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          name="total_assets"
                          value={formData.total_assets}
                          onChange={handleChange}
                          className="input-institutional pl-8 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.totalLiabilities}</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          name="total_liabilities"
                          value={formData.total_liabilities}
                          onChange={handleChange}
                          className="input-institutional pl-8 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.annualIncome}</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          name="annual_income"
                          value={formData.annual_income}
                          onChange={handleChange}
                          className="input-institutional pl-8 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.liquidAssets}</Label>
                      <div className="relative mt-2">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          name="liquid_assets"
                          value={formData.liquid_assets}
                          onChange={handleChange}
                          className="input-institutional pl-8 rounded-none"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-body font-medium text-slate-700">{l.creditScore}</Label>
                      <Select onValueChange={(v) => handleSelectChange('credit_score_range', v)} value={formData.credit_score_range}>
                        <SelectTrigger className="input-institutional mt-2 rounded-none">
                          <SelectValue placeholder={l.selectCreditScore} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="below_600">Below 600</SelectItem>
                          <SelectItem value="600_649">600 - 649</SelectItem>
                          <SelectItem value="650_699">650 - 699</SelectItem>
                          <SelectItem value="700_749">700 - 749</SelectItem>
                          <SelectItem value="750_plus">750+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Documents & Submit */}
            {currentStep === 4 && (
              <div className="space-y-8" data-testid="step-4">
                {/* File Upload */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">{l.documents}</h2>
                  <p className="font-body text-slate-600 mb-8">{l.documentsDesc}</p>
                  
                  <div className="border-2 border-dashed border-slate-300 rounded-none p-8 text-center hover:border-slate-400 transition-colors duration-200">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="font-body text-slate-600 mb-2">{l.dragDrop}</p>
                      <p className="font-body text-slate-400 text-sm">{l.maxSize}</p>
                    </label>
                  </div>
                  
                  {uploadedFiles.length > 0 && (
                    <div className="mt-6 space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-50 p-3 border border-slate-200">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-slate-500" />
                            <span className="font-body text-sm text-slate-700">{file.name}</span>
                            <span className="font-body text-xs text-slate-400">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Additional Notes */}
                <div className="bg-white border border-slate-200 p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">{l.additionalNotes}</h2>
                  
                  <Textarea
                    name="additional_notes"
                    value={formData.additional_notes}
                    onChange={handleChange}
                    className="input-institutional rounded-none min-h-[150px]"
                    placeholder={l.additionalPlaceholder}
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {currentStep > 1 ? (
                <Button
                  type="button"
                  onClick={prevStep}
                  className="btn-secondary rounded-none"
                >
                  {l.previous}
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="btn-gold rounded-none"
                >
                  {l.next}
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-gold rounded-none"
                  data-testid="submit-application"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {l.submitting}
                    </>
                  ) : (
                    l.submit
                  )}
                </Button>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
